import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, MessageSquare, Send, Loader2, X, Check, ExternalLink, ShoppingBag, CheckCheck, Mic, UserPlus,
} from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import ModalShell from '../ui/ModalShell';
import Dropdown from '../receptura/Dropdown';
import { SECTION_COLOR, SECTION_BG, CERVENA, fmtKc, Nadpis, RadekPanelu, hlavicky } from '../objednavky/ui';

/**
 * Zákazníci → Komunikace (od 2026-10-12). Norma §1: schránka · vlákno ·
 * třetí sloupec (`panelVzdy` — čísla dne a „K dořešení").
 *
 * Vlákno = jedno číslo (posledních 9 číslic), takže i neznámé číslo má
 * svou historii. Data sbírá telefon bistra (FofrTelefon) a pokladna; tady
 * se k hovoru píše poznámka, výsledek a vyřízení, volá se a píše přes
 * tentýž telefon. Vyřízení se propisuje do oznámení v pokladně a naopak.
 * Obnova jednou za 30 s, když je stránka vidět.
 */

const POHLEDY = [
    { id: 'vse', label: 'Vše' },
    { id: 'nevyrizene', label: 'Nevyřízené', upozorneni: true },
    { id: 'zmeskane', label: 'Zmeškané' },
    { id: 'sms', label: 'SMS' },
    { id: 'nezname', label: 'Neznámá čísla' },
];

const VYSLEDKY = [
    { value: '', label: '— bez výsledku' },
    { value: 'objednavka', label: 'Objednávka' },
    { value: 'rezervace', label: 'Rezervace' },
    { value: 'dotaz', label: 'Dotaz' },
    { value: 'reklamace', label: 'Reklamace' },
    { value: 'nedovolano', label: 'Nedovoláno' },
    { value: 'jine', label: 'Jiné' },
];

const SMS_TYP = { nezastizen: 'nezastihli jsme vás', za_rohem: 'kurýr za rohem', uctenka_sms: 'účtenka', pokladna: 'z pokladny', os: 'z OS', zkouska: 'zkouška' };
const HOVOR_STAV = { zmeskano: 'zmeškaný', nedostupne: 'nedovoláno', zvoni: 'zvoní', probiha: 'probíhá', vytaci: 'vytáčí se', ukonceno: '' };

const hezkeCislo = (c) => {
    const m = /^\+420(\d{3})(\d{3})(\d{3})$/.exec(String(c || '').replace(/\s/g, ''));
    return m ? `+420 ${m[1]} ${m[2]} ${m[3]}` : c;
};
const delka = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');
const kdyKratce = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dnes = new Date();
    const cas = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === dnes.toDateString()) return cas;
    const vcera = new Date(dnes); vcera.setDate(dnes.getDate() - 1);
    if (d.toDateString() === vcera.toDateString()) return `včera ${cas}`;
    return `${d.getDate()}. ${d.getMonth() + 1}. ${cas}`;
};

/** Poslední událost vlákna jedním řádkem. */
function popisUdalosti(u) {
    if (!u) return '';
    if (u.druh === 'hovor') {
        if (u.stav === 'zmeskano') return 'Zmeškaný hovor';
        const smer = u.smer === 'odchozi' ? 'Odchozí hovor' : 'Hovor';
        return `${smer}${u.delka_s ? ` ${delka(u.delka_s)}` : ''}${HOVOR_STAV[u.stav] ? ` · ${HOVOR_STAV[u.stav]}` : ''}`;
    }
    if (u.druh === 'sms_dovnitr') return `SMS: ${u.text || ''}`;
    return `Odesláno: ${u.text || SMS_TYP[u.typ] || ''}`;
}

async function posli(url, metoda, telo) {
    const r = await fetch(url, { method: metoda, headers: hlavicky(), body: telo ? JSON.stringify(telo) : undefined });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.message || 'Akce se nepovedla.');
    return j;
}

/** Položky „K dořešení" pro panel i mobilní modal (§1g) — z jednoho přehledu. */
function kDoreseniKomunikace(prehled) {
    if (!prehled) return [];
    const p = [];
    if (prehled.telefon && !prehled.telefon.online) {
        p.push({ id: 'offline', problem: true, titulek: 'Telefon bistra je offline', popis: 'hovory a SMS teď nedorazí do systému' });
    }
    for (const v of prehled.nevyrizene || []) {
        p.push({
            id: `v-${v.klic}`, cislo: v.cislo, problem: v.zmeskane > 0,
            titulek: `${v.jmeno || hezkeCislo(v.cislo)}`,
            popis: [v.zmeskane ? `${v.zmeskane}× zmeškaný hovor` : null, v.sms_nevyrizene ? `${v.sms_nevyrizene}× SMS` : null].filter(Boolean).join(' · ') + ` · ${kdyKratce(v.posledni?.kdy)}`,
        });
    }
    return p;
}

export default function KomunikaceModul({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, otevritCislo = null, onZakaznik, onObjednavka }) {
    const [data, setData] = useState(null);
    const [prehled, setPrehled] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [pohled, setPohled] = useState('vse');
    const [hledani, setHledani] = useState('');
    const [hledaniServer, setHledaniServer] = useState('');
    const [vybrane, setVybrane] = useState(otevritCislo);
    const [obnova, setObnova] = useState(0);
    const [psat, setPsat] = useState(null); // číslo, kam psát SMS

    useEffect(() => { if (otevritCislo) setVybrane(otevritCislo); }, [otevritCislo]);
    useEffect(() => {
        const t = setTimeout(() => setHledaniServer(hledani.trim()), 350);
        return () => clearTimeout(t);
    }, [hledani]);

    const nacti = useCallback(async () => {
        const p = new URLSearchParams({ pohled });
        if (hledaniServer) p.set('q', hledaniServer);
        try {
            const r = await fetch(`/api/zakaznici/komunikace?${p}`, { headers: { Accept: 'application/json' } });
            if (!r.ok) throw new Error(r.status === 403 ? 'Nemáte přístup ke komunikaci se zákazníky.' : 'Schránku se nepodařilo načíst.');
            setData(await r.json());
            setChyba(null);
        } catch (e) { setChyba(e.message); setData(d => d || { vlakna: [], pocty: {} }); }
    }, [pohled, hledaniServer]);
    const nactiPrehled = useCallback(() => {
        fetch('/api/zakaznici/komunikace/prehled', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null)).then(d => d && setPrehled(d)).catch(() => {});
    }, []);
    const obnovVse = useCallback(() => { nacti(); nactiPrehled(); setObnova(n => n + 1); }, [nacti, nactiPrehled]);

    useEffect(() => { nacti(); }, [nacti]);
    useEffect(() => { nactiPrehled(); }, [nactiPrehled]);
    useEffect(() => {
        const t = setInterval(() => { if (!document.hidden) obnovVse(); }, 30000);
        return () => clearInterval(t);
    }, [obnovVse]);

    const kDoreseni = useMemo(() => kDoreseniKomunikace(prehled), [prehled]);
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const pocty = data?.pocty || {};

    const otevriAkce = (p) => (p.cislo ? (
        <button type="button" onClick={() => setVybrane(p.cislo)} className="inline-flex items-center"
            style={{ gap: 5, fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minHeight: 28 }}>
            Otevřít <ExternalLink size={12} />
        </button>
    ) : null);

    const souhrn = () => {
        if (!prehled) return <p className="text-xs" style={{ color: muted }}>Načítám…</p>;
        const d = prehled.dnes;
        const maxH = Math.max(1, ...d.po_hodinach);
        const hodiny = d.po_hodinach.map((n, h) => ({ h, n })).filter(x => x.h >= 8 && x.h <= 22);
        return (
            <>
                <Nadpis muted={muted}>Dnes</Nadpis>
                <RadekPanelu popis="Přijaté hovory" hodnota={d.prijate} muted={muted} cara={cara} />
                <RadekPanelu popis="Zmeškané hovory" hodnota={d.zmeskane} cervene={d.zmeskane > 0} muted={muted} cara={cara} />
                <RadekPanelu popis="Odchozí hovory" hodnota={d.odchozi} muted={muted} cara={cara} />
                <RadekPanelu popis="SMS přijaté · odeslané" hodnota={`${d.sms_dovnitr} · ${d.sms_ven}`} muted={muted} cara={cara} />
                <RadekPanelu popis="Zpětné volání průměrně" hodnota={d.zpetne_volani_min != null ? `${d.zpetne_volani_min} min` : '—'} muted={muted} cara={cara} />
                <Nadpis muted={muted} style={{ marginTop: 16 }}>Příchozí hovory po hodinách</Nadpis>
                <div className="flex items-end" style={{ gap: 3, height: 44 }} aria-label="Příchozí hovory po hodinách">
                    {hodiny.map(x => (
                        <div key={x.h} title={`${x.h}:00 · ${x.n} hovorů`} style={{ flex: 1, height: `${Math.max(4, (x.n / maxH) * 100)}%`, borderRadius: 2, background: x.n ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') }} />
                    ))}
                </div>
                <div className="flex justify-between" style={{ fontSize: 10.5, color: muted, marginTop: 4 }}><span>8</span><span>15</span><span>22</span></div>
            </>
        );
    };

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column' }}>
            {souhrn()}
            <Nadpis muted={muted} style={{ marginTop: 16 }}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} renderAkce={otevriAkce} prazdne="Všechno vyřízené." />
        </div>
    );

    const radek = (v) => {
        const vybrany = vybrane && v.klic === String(vybrane).replace(/\D/g, '').slice(-9);
        const nevyrizeno = v.zmeskane + v.sms_nevyrizene;
        const u = v.posledni;
        const Ikona = u?.druh === 'hovor' ? (u.stav === 'zmeskano' ? PhoneMissed : u.smer === 'odchozi' ? PhoneOutgoing : PhoneIncoming) : MessageSquare;
        return (
            <button key={v.klic} type="button" onClick={() => setVybrane(v.cislo)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: isMobile ? '10px 16px' : '9px 14px', minHeight: isMobile ? 62 : 56,
                    border: 'none', borderBottom: `1px solid ${cara}`,
                    background: vybrany ? SECTION_BG : 'transparent', boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
                }}>
                <Ikona size={16} style={{ flexShrink: 0, color: u?.stav === 'zmeskano' && v.zmeskane ? CERVENA : muted }} />
                <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ margin: 0, fontSize: isMobile ? 15 : 13.5, fontWeight: nevyrizeno ? 700 : 600, color: 'var(--text-primary)' }}>
                        {v.jmeno || hezkeCislo(v.cislo)}
                    </p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 12.5 : 11.5, color: muted }}>{popisUdalosti(u)}</p>
                </div>
                <div className="flex flex-col items-end" style={{ gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 11.5, color: muted, fontVariantNumeric: 'tabular-nums' }}>{kdyKratce(u?.kdy)}</span>
                    {nevyrizeno > 0 && (
                        <span style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, fontSize: 10.5, fontWeight: 700, lineHeight: '18px', textAlign: 'center', color: '#fff', background: CERVENA }}>{nevyrizeno}</span>
                    )}
                </div>
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat jméno, číslo nebo text SMS…', placeholderKratky: 'Hledat' }}
                akce={[{ id: 'sms', label: 'Napsat SMS', Icon: Send, onClick: () => setPsat(''), primarni: true }]}
                pod={isMobile ? (
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} renderAkce={otevriAkce}
                        navic={<div style={{ padding: '14px 16px' }}>{souhrn()}</div>} />
                ) : null}
                pohledy={!siroky && !isMobile ? POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? CERVENA : undefined })) : null}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id] ?? null} aktivni={pohled === p.id}
                        upozorneni={p.upozorneni && (pocty[p.id] || 0) > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {chyba && <p className="text-sm text-center" style={{ color: CERVENA, padding: '24px 16px' }}>{chyba}</p>}
                {!data && !chyba && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && !chyba && data.vlakna.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledaniServer ? 'Nic se neshoduje.' : pohled === 'nevyrizene' ? 'Všechno vyřízené.' : `Za posledních ${data.dni || 60} dní tu nic není.`}
                    </p>
                )}
                {(data?.vlakna || []).map(radek)}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <Vlakno key={vybrane} cislo={vybrane} obnova={obnova} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara}
                onZpet={() => setVybrane(null)} onZmena={() => { nacti(); nactiPrehled(); }} onPsat={setPsat}
                onZakaznik={onZakaznik} onObjednavka={onObjednavka} rezervaDole={isMobile ? rezervaDole : null} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybrane} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detail} panel={panel} />
            {psat !== null && (
                <NapsatSms cislo={psat} isDark={isDark} muted={muted} inputStyle={inputStyle} onClose={() => setPsat(null)}
                    onOdeslano={(c) => { setPsat(null); setVybrane(c); obnovVse(); }} />
            )}
        </div>
    );
}

/** Vlákno s jedním číslem — hovory a SMS na jedné ose, nejnovější dole. */
function Vlakno({ cislo, obnova, isDark, isMobile, muted, cara, onZpet, onZmena, onPsat, onZakaznik, onObjednavka, rezervaDole }) {
    const [d, setD] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [prace, setPrace] = useState(null);
    const [hlaska, setHlaska] = useState(null);
    const [prirazovat, setPrirazovat] = useState(false);

    const nacti = useCallback(() => {
        fetch(`/api/zakaznici/komunikace/vlakno?cislo=${encodeURIComponent(cislo)}`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error('Vlákno se nepodařilo načíst.'))))
            .then(x => { setD(x); setChyba(null); })
            .catch(e => setChyba(e.message));
    }, [cislo]);
    useEffect(() => { nacti(); }, [nacti, obnova]);

    const akce = async (klic, fn, zprava = null) => {
        setPrace(klic); setHlaska(null);
        try { await fn(); if (zprava) setHlaska({ ok: true, text: zprava }); nacti(); onZmena?.(); }
        catch (e) { setHlaska({ ok: false, text: e.message }); }
        finally { setPrace(null); }
    };

    if (chyba) return <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>{chyba}</p>;
    if (!d) return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: muted }}><Loader2 size={24} className="animate-spin" /></div>;

    const z = d.zakaznik;
    const nevyrizenych = d.udalosti.filter(u => u.nevyrizeno).length;
    const tlacitko = (primarni) => ({
        display: 'inline-flex', alignItems: 'center', gap: 7, height: isMobile ? 44 : 36, padding: '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        color: primarni ? '#fff' : 'var(--text-primary)', background: primarni ? SECTION_COLOR : 'transparent',
        border: primarni ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        flex: isMobile ? 1 : undefined, justifyContent: 'center',
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ padding: isMobile ? '6px 16px 12px' : '16px 22px 14px', flexShrink: 0, borderBottom: `1px solid ${cara}` }}>
                {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět do schránky" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                <div className="flex items-start" style={{ gap: 12 }}>
                    <div className="min-w-0 flex-1">
                        <h2 className="truncate" style={{ margin: 0, fontSize: isMobile ? 19 : 18, fontWeight: 700, color: 'var(--text-primary)' }}>{z?.jmeno || hezkeCislo(d.cislo)}</h2>
                        <p style={{ margin: '3px 0 0', fontSize: 12.5, color: muted }}>
                            {z ? `${hezkeCislo(d.cislo)} · ${z.objednavek} objednávek${z.adresa ? ` · ${z.adresa}` : ''}` : 'Neznámé číslo — v evidenci zákazníků není'}
                        </p>
                        {z?.poznamka && <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>Poznámka pro obsluhu: {z.poznamka}</p>}
                    </div>
                    {!isMobile && (
                        <button type="button" onClick={onZpet} title="Zavřít" aria-label="Zavřít" className="flex items-center justify-center rounded-lg"
                            style={{ width: 34, height: 34, flexShrink: 0, cursor: 'pointer', color: 'var(--text-secondary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                            <X size={16} />
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap" style={{ gap: 8, marginTop: 12 }}>
                    {d.muze_upravit && (
                        <>
                            <button type="button" disabled={!!prace} style={tlacitko(true)}
                                onClick={() => akce('volat', () => posli('/api/zakaznici/komunikace/vytocit', 'POST', { cislo: d.cislo }), 'Telefon bistra vytáčí.')}>
                                {prace === 'volat' ? <Loader2 size={15} className="animate-spin" /> : <Phone size={15} />} Zavolat
                            </button>
                            <button type="button" style={tlacitko(false)} onClick={() => onPsat(d.cislo)}><Send size={15} /> Napsat SMS</button>
                            {nevyrizenych > 0 && (
                                <button type="button" disabled={!!prace} style={tlacitko(false)}
                                    onClick={() => akce('vse', () => posli('/api/zakaznici/komunikace/vlakno/vyridit', 'POST', { cislo: d.cislo }))}>
                                    <CheckCheck size={15} /> Vyřídit vše ({nevyrizenych})
                                </button>
                            )}
                        </>
                    )}
                    {z && onZakaznik && <button type="button" style={tlacitko(false)} onClick={() => onZakaznik(z.id)}><ExternalLink size={15} /> Karta hosta</button>}
                    {d.muze_upravit && <button type="button" style={tlacitko(false)} onClick={() => setPrirazovat(true)}><UserPlus size={15} /> {z ? "Jiný host" : "Přiřadit hosta"}</button>}
                </div>
                {hlaska && <p style={{ margin: '8px 0 0', fontSize: 12.5, color: hlaska.ok ? SECTION_COLOR : CERVENA }}>{hlaska.text}</p>}
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '8px 16px' : '10px 22px' }}>
                {d.udalosti.length === 0 && <p className="text-sm text-center" style={{ color: muted, padding: 24 }}>S tímhle číslem zatím žádná komunikace.</p>}
                {d.udalosti.map(u => (u.druh === 'hovor' ? (
                    <Hovor key={`h${u.id}`} u={u} muzeUpravit={d.muze_upravit} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara} onZmena={() => { nacti(); onZmena?.(); }} onObjednavka={onObjednavka} />
                ) : (
                    <Bublina key={`${u.druh}${u.id}`} u={u} muzeUpravit={d.muze_upravit} isDark={isDark} muted={muted}
                        onVyridit={() => akce(`sms${u.id}`, () => posli(`/api/zakaznici/komunikace/sms/${u.id}/vyridit`, 'POST'))} />
                )))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
            {prirazovat && (
                <PriraditHosta cislo={d.cislo} isDark={isDark} muted={muted} onClose={() => setPrirazovat(false)}
                    onHotovo={() => { setPrirazovat(false); nacti(); onZmena?.(); }} />
            )}
        </div>
    );
}

/**
 * Přiřadit číslo hostovi — hovory a SMS s tímhle číslem dostanou hosta
 * a číslo se mu přidá do kontaktů, takže příště ho pozná i pokladna.
 */
function PriraditHosta({ cislo, isDark, muted, onClose, onHotovo }) {
    const [q, setQ] = useState('');
    const [hoste, setHoste] = useState([]);
    const [chyba, setChyba] = useState(null);
    const [prace, setPrace] = useState(false);

    useEffect(() => {
        if (q.trim().length < 2) { setHoste([]); return undefined; }
        const t = setTimeout(() => {
            fetch(`/api/zakaznici/komunikace/hledat-hosta?q=${encodeURIComponent(q.trim())}`, { headers: { Accept: 'application/json' } })
                .then(r => (r.ok ? r.json() : { hoste: [] })).then(x => setHoste(x.hoste || [])).catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    const vybrat = async (h) => {
        setPrace(true); setChyba(null);
        try { await posli('/api/zakaznici/komunikace/vlakno/priradit', 'POST', { cislo, zakaznik_id: h.id }); onHotovo(); }
        catch (e) { setChyba(e.message); setPrace(false); }
    };

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={`Číslo ${hezkeCislo(cislo)} patří…`} ikona={UserPlus} barva={SECTION_COLOR} sirka={480}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Hledat hosta podle jména, telefonu nebo e-mailu"
                    style={{ height: 42, borderRadius: 8, padding: '0 12px', fontSize: 14, outline: 'none', color: 'var(--text-primary)',
                        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }} />
                {hoste.map(h => (
                    <button key={h.id} type="button" disabled={prace} onClick={() => vybrat(h)} className="text-left"
                        style={{ padding: '10px 12px', minHeight: 52, borderRadius: 10, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{h.jmeno || 'Bez jména'}</span>
                        <span style={{ display: 'block', fontSize: 12, color: muted }}>{[h.telefon, h.email, h.adresa].filter(Boolean).join(' · ') || 'bez kontaktu'}</span>
                    </button>
                ))}
                {q.trim().length >= 2 && hoste.length === 0 && <p style={{ margin: 0, fontSize: 12.5, color: muted }}>Nikdo takový. Nového hosta založí pokladna při první objednávce.</p>}
                {chyba && <p style={{ margin: 0, fontSize: 12.5, color: CERVENA }}>{chyba}</p>}
                <p style={{ margin: 0, fontSize: 11.5, color: muted }}>Hovory a SMS s tímhle číslem se přiřadí k vybranému hostovi a číslo se mu přidá do kontaktů.</p>
            </div>
        </ModalShell>
    );
}

/** SMS jako bublina — příchozí vlevo, odeslaná vpravo. */
function Bublina({ u, muzeUpravit, isDark, muted, onVyridit }) {
    const ven = u.druh === 'sms_ven';
    const chyba = ven && u.stav === 'chyba';
    return (
        <div className="flex flex-col" style={{ alignItems: ven ? 'flex-end' : 'flex-start', margin: '8px 0' }}>
            <div style={{
                maxWidth: '82%', padding: '8px 12px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.4, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                background: ven ? SECTION_BG : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                border: `1px solid ${u.nevyrizeno ? `${CERVENA}66` : ven ? `${SECTION_COLOR}40` : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`,
            }}>{u.text}</div>
            <span style={{ fontSize: 11, color: chyba ? CERVENA : muted, marginTop: 3 }}>
                {kdyKratce(u.kdy)}
                {ven && u.typ ? ` · ${SMS_TYP[u.typ] || u.typ}` : ''}
                {ven && u.test ? ' · test, neodesláno' : ''}
                {chyba ? ' · neodešla' : ven && u.stav === 'doruceno' ? ' · doručeno' : ''}
                {!ven && u.nevyrizeno && muzeUpravit && (
                    <button type="button" onClick={onVyridit} style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                        Vyřízeno
                    </button>
                )}
            </span>
        </div>
    );
}

/** Hovor ve vláknu — stav, délka, propojená objednávka, poznámka, výsledek, vyřízeno. */
function Hovor({ u, muzeUpravit, isDark, isMobile, muted, cara, onZmena, onObjednavka }) {
    const [poznamka, setPoznamka] = useState(u.poznamka || '');
    const [uklada, setUklada] = useState(false);
    const zmeskany = u.stav === 'zmeskano';
    const Ikona = zmeskany ? PhoneMissed : u.smer === 'odchozi' ? PhoneOutgoing : PhoneIncoming;
    const barva = u.nevyrizeno ? CERVENA : muted;

    const uloz = async (zmena) => {
        setUklada(true);
        try { await posli(`/api/zakaznici/komunikace/hovor/${u.id}`, 'PATCH', zmena); onZmena(); }
        catch { /* chyba se projeví neuložením — vlákno se znovu načte */ }
        finally { setUklada(false); }
    };

    return (
        <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 10, border: `1px solid ${u.nevyrizeno ? `${CERVENA}55` : cara}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' }}>
            <div className="flex items-center" style={{ gap: 10 }}>
                <Ikona size={16} style={{ color: barva, flexShrink: 0 }} />
                <span className="flex-1 min-w-0" style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
                    <b style={{ fontWeight: 600 }}>{zmeskany ? 'Zmeškaný hovor' : u.smer === 'odchozi' ? 'Odchozí hovor' : 'Příchozí hovor'}</b>
                    <span style={{ color: muted }}> · {kdyKratce(u.kdy)}{u.delka_s ? ` · ${delka(u.delka_s)}` : ''}{!zmeskany && HOVOR_STAV[u.stav] ? ` · ${HOVOR_STAV[u.stav]}` : ''}{u.kdo ? ` · ${u.kdo}` : ''}</span>
                </span>
                {u.vyrizeno_v && <span title={u.vyridil ? `vyřídil(a) ${u.vyridil}` : 'vyřízeno'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: SECTION_COLOR }}><Check size={13} /> vyřízeno</span>}
            </div>

            {u.objednavka && (
                <button type="button" onClick={() => onObjednavka?.(u.objednavka.id)} disabled={!onObjednavka}
                    className="inline-flex items-center" style={{ gap: 6, marginTop: 8, fontSize: 12.5, color: 'var(--text-primary)', background: 'transparent', border: 'none', padding: 0, cursor: onObjednavka ? 'pointer' : 'default' }}>
                    <ShoppingBag size={13} style={{ color: SECTION_COLOR }} /> Objednávka #{u.objednavka.cislo} · {fmtKc(u.objednavka.celkem_s_dph)}
                </button>
            )}

            {/* Nahrávka sdílená z aplikace Telefon přes FofrTelefon — přehrát smí jen admin */}
            {u.nahravka ? (
                <audio controls preload="none" src={`/api/zakaznici/komunikace/nahravka/${u.nahravka.id}`}
                    style={{ display: 'block', width: '100%', height: 36, marginTop: 8 }} />
            ) : u.ma_nahravku ? (
                <p className="inline-flex items-center" style={{ gap: 6, margin: '8px 0 0', fontSize: 12, color: muted }}>
                    <Mic size={13} /> Hovor je nahraný — přehrát ho smí jen admin.
                </p>
            ) : null}

            {muzeUpravit ? (
                <div className="flex flex-wrap items-center" style={{ gap: 8, marginTop: 10 }}>
                    <div style={{ width: isMobile ? '100%' : 170 }}>
                        <Dropdown value={u.vysledek || ''} onChange={(v) => uloz({ vysledek: v || null })} options={VYSLEDKY}
                            placeholder="Výsledek" isDark={isDark} accent={SECTION_COLOR} height={isMobile ? 44 : 34} />
                    </div>
                    <input value={poznamka} onChange={e => setPoznamka(e.target.value)} onBlur={() => poznamka !== (u.poznamka || '') && uloz({ poznamka })}
                        placeholder="Poznámka k hovoru" maxLength={2000}
                        style={{ flex: 1, minWidth: 160, height: isMobile ? 44 : 34, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', color: 'var(--text-primary)',
                            background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }} />
                    {(u.nevyrizeno || (zmeskany && !u.vyrizeno_v)) && (
                        <button type="button" disabled={uklada} onClick={() => uloz({ vyrizeno: true })}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: isMobile ? 44 : 34, padding: '0 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: '#fff', background: SECTION_COLOR, border: 'none', width: isMobile ? '100%' : undefined, justifyContent: 'center' }}>
                            {uklada ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Vyřízeno
                        </button>
                    )}
                </div>
            ) : (u.vysledek || u.poznamka) && (
                <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {[VYSLEDKY.find(v => v.value === u.vysledek)?.label, u.poznamka].filter(Boolean).join(' · ')}
                </p>
            )}
        </div>
    );
}

/** Napsat SMS přes telefon bistra — modal (§1f: krátká akce). */
function NapsatSms({ cislo: vychozi, isDark, muted, inputStyle, onClose, onOdeslano }) {
    const [cislo, setCislo] = useState(vychozi || '');
    const [text, setText] = useState('');
    const [sablony, setSablony] = useState([]);
    useEffect(() => {
        fetch('/api/zakaznici/sablony?kanal=sms&jen_aktivni=1', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : { sablony: [] })).then(x => setSablony(x.sablony || [])).catch(() => {});
    }, []);
    const [prace, setPrace] = useState(false);
    const [chyba, setChyba] = useState(null);
    // GSM 7bit: 160 znaků, s diakritikou (UCS-2) 70; delší zpráva = víc částí
    const ucs2 = /[^\x20-\x7E\n\r]/.test(text);
    const nacast = ucs2 ? 70 : 160;
    const casti = text.length <= nacast ? 1 : Math.ceil(text.length / (ucs2 ? 67 : 153));

    const odeslat = async () => {
        setPrace(true); setChyba(null);
        try {
            const r = await posli('/api/zakaznici/komunikace/sms', 'POST', { cislo, text });
            // Testovací režim: zpráva se jen uložila — modal nechat otevřený s vysvětlením
            if (r.test) { setChyba(r.message); return; }
            onOdeslano(cislo);
        } catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Napsat SMS" ikona={Send} barva={SECTION_COLOR} sirka={480}
            paticka={(
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="rounded-lg"
                        style={{ height: 40, padding: '0 16px', fontSize: 13, cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                        Zrušit
                    </button>
                    <button type="button" onClick={odeslat} disabled={prace || !text.trim() || cislo.replace(/\D/g, '').length < 9} className="rounded-lg font-semibold"
                        style={{ height: 40, padding: '0 16px', fontSize: 13, cursor: 'pointer', background: SECTION_COLOR, color: '#fff', border: 'none', opacity: prace || !text.trim() ? 0.6 : 1 }}>
                        {prace ? 'Odesílám…' : 'Odeslat'}
                    </button>
                </div>
            )}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input value={cislo} onChange={e => setCislo(e.target.value)} placeholder="Telefonní číslo" inputMode="tel" style={{ ...inputStyle, height: 40 }} />
                {sablony.length > 0 && (
                    <Dropdown value={null} onChange={(id) => { const s = sablony.find(x => x.id === id); if (s) setText(s.text); }}
                        options={sablony.map(s => ({ value: s.id, label: s.nazev, popis: s.ucel === 'marketing' ? 'marketing — jen se souhlasem' : 'provozní' }))}
                        placeholder="Použít šablonu…" isDark={isDark} accent={SECTION_COLOR} height={40} />
                )}
                <textarea value={text} onChange={e => setText(e.target.value)} rows={5} placeholder="Text zprávy" maxLength={1000}
                    style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical', lineHeight: 1.4 }} />
                <span style={{ fontSize: 11.5, color: muted }}>{text.length} znaků · {casti} SMS{ucs2 ? ' · s diakritikou se do jedné SMS vejde 70 znaků' : ''}</span>
                {chyba && <p style={{ margin: 0, fontSize: 12.5, color: CERVENA }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}
